import { Component, effect, inject, Input, ViewChild, OnInit } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { DialogObject } from '@/components/dialog/dialog.types';
import { DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { SeuilsGlobalService } from './seuils-global.service';
import { getSeuilDataTableStructure } from './utils/seuil-data-table-object';
import { SeuilsGlobalRepository } from '@/store/seuils-global-state/seuils-global.repository';
import { DynamicForm } from '@/components/form-builder/form-builder.types';
import { getAddSeuilDialogObject } from './utils/seuil-add-dialog-object';
import { getSeuilFormObject } from './utils/seuil-data-form-object';
import { getUpdateSeuilDialogObject } from './utils/seuil-update-dialog-object';
import { getDeleteSeuilDialogObject } from './utils/seuil-delete-dialog-object';
import { map, Observable } from 'rxjs';
import { AuthorizationService } from '@/services/authorization.service';
import { UserRole } from '@/types/user.types';
import { RefParamsService } from '../ref-params/ref-params.service';
import { ContractingAuthorityService } from '../contracting-authority/contracting-authority.service';
import { RefTypeEnum } from '@/types';
import { CommonModule } from '@angular/common';
import { createServerQuery } from '@/components/data-table-builder/server-query';
import { DataTableServerQuery } from '@/components/data-table-builder/data-table.types';

interface SeuilGlobal {
  id: number;
  typeMarcherId: number;
  montant: number;
  typeMarcher?: {
    id: number;
    description: string;
  };
}

interface SelectOption {
  name: string;
  code: number;
}

@Component({
  selector: 'sm-seuils-global',
  imports: [DataTableBuilderComponent, DialogComponent, TranslateModule, CommonModule],
  templateUrl: './seuils-global.component.html',
  styleUrl: './seuils-global.component.scss',
})
export class SeuilsGlobalComponent implements OnInit {
  settingsSeuilsService = inject(SeuilsGlobalService);
  settingsSeuilsRepository = inject(SeuilsGlobalRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  authService = inject(AuthorizationService);
  refParamsService = inject(RefParamsService);
  contractingAuthorityService = inject(ContractingAuthorityService);

  @Input() acId?: number;
  @Input() isInspectMode?: boolean;

  typeMarcherOptions: SelectOption[] = [];
  optionsLoaded = false;

  isAddDisabled$: Observable<boolean> = this.settingsSeuilsRepository.getNestedObservable<number>('dto', 'montant').pipe(
    map(montant => {
      return !montant || montant <= 0;
    })
  );
  isUpdateDisabled$: Observable<boolean> = this.settingsSeuilsRepository.getNestedObservable<number>('dto', 'montant').pipe(
    map(montant => {
      const serverState = this.settingsSeuilsRepository.get('serverState') as any;
      return serverState?.montant === montant;
    })
  );

  seuilFormObject: DynamicForm = getSeuilFormObject({
    store: this.settingsSeuilsRepository,
    typeMarcherOptions: [],
  });

  @ViewChild('delete') deleteDialogComponent!: DialogComponent;
  deleteDialog?: DialogObject;

  @ViewChild('addDialog') addSeuilComponent!: DialogComponent;
  addSeuilDialog?: DialogObject;

  @ViewChild('updateDialog') updateSeuilComponent!: DialogComponent;
  updateSeuilDialog?: DialogObject;

  dataTableObject?: DynamicDataTable;
  data: SeuilGlobal[] = [];
  totalRecords = 0;
  loading = false;
  serverQuery: DataTableServerQuery = createServerQuery({});

  getRowActions(): DataTableRowActions {
    return {
      editAction: this.authService.hasRoles([UserRole.ADMIN_CENTRAL, UserRole.SUPER_ADMIN])
        ? () => {
            if (this.optionsLoaded) {
              const currentDto = this.settingsSeuilsRepository.get('dto') as any;
              const currentResponse = this.settingsSeuilsRepository.get('response') as any;

              if (!currentResponse || !currentResponse.id) {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Erreur',
                  detail: this.translate.instant('settings.seuils.message.error.loading'),
                });
                return;
              }

              this.settingsSeuilsRepository.set('serverState', {
                montant: currentDto.montant,
                typeMarcherId: currentDto.typeMarcherId,
              });
              this.updateSeuilDialog = getUpdateSeuilDialogObject({
                formObject: this.createUpdateFormObject(),
                action: () => this.updateSeuil(),
                toggleVisibility: () => {
                  this.settingsSeuilsRepository.reset();
                  this.updateSeuilComponent.toggleVisible();
                },
                isUpdateDisabled: this.isUpdateDisabled$,
              });
              this.updateSeuilComponent.toggleVisible();
            } else {
              this.messageService.add({
                severity: 'warn',
                summary: 'Attention',
                detail: this.translate.instant('settings.seuils.message.loading'),
              });
            }
          }
        : undefined,
      deleteAction: this.authService.hasRoles([UserRole.ADMIN_CENTRAL, UserRole.SUPER_ADMIN])
        ? (row: any) => {
            this.settingsSeuilsRepository.set('response', row);
            this.deleteDialog = getDeleteSeuilDialogObject({
              action: () => this.deleteSeuil(),
              toggleVisibility: () => this.deleteDialogComponent.toggleVisible(),
            });
            this.deleteDialogComponent.toggleVisible();
          }
        : undefined,
    };
  }

  ngOnInit(): void {
    // Load both options together and create dialog only after both are loaded
    this.refParamsService.getSpecializedRefParams(RefTypeEnum.MARKET_TYPE).subscribe({
      next: (refs: any) => {
        this.typeMarcherOptions = refs.map((ref: any) => ({ name: ref.description, code: ref.id }));
        this.checkOptionsLoaded();
      },
      error: () => {
        this.typeMarcherOptions = [];
        this.checkOptionsLoaded();
      },
    });

    this.dataTableObject = getSeuilDataTableStructure({
      store: this.settingsSeuilsRepository,
      createAction: this.isInspectMode
        ? undefined
        : () => {
            if (this.optionsLoaded) {
              this.settingsSeuilsRepository.reset();
              this.addSeuilComponent.toggleVisible();
            } else {
              this.messageService.add({
                severity: 'warn',
                summary: 'Attention',
                detail: this.translate.instant('settings.seuils.message.loading'),
              });
            }
          },
      rowActions: this.isInspectMode ? undefined : this.getRowActions(),
      serverQuery: this.serverQuery,
    });
  }
  constructor() {
    effect(() => {
      this.serverQuery.page();
      this.serverQuery.pageSize();
      this.serverQuery.search();
      this.serverQuery.sortBy();
      this.serverQuery.sortOrder();
      this.getSeuils();
    });
  }
  checkOptionsLoaded() {
    this.optionsLoaded = true;
    this.updateFormObject();
    this.createAddDialog();
  }

  createAddDialog() {
    this.addSeuilDialog = getAddSeuilDialogObject({
      formObject: this.seuilFormObject, // Use the real form object
      action: () => this.addSeuil(),
      isAddDisabled: this.isAddDisabled$,
      toggleVisibility: () => this.addSeuilComponent.toggleVisible(),
    });
  }

  updateFormObject() {
    this.seuilFormObject = getSeuilFormObject({
      store: this.settingsSeuilsRepository,
      typeMarcherOptions: this.typeMarcherOptions,
    });
  }

  createUpdateFormObject() {
    return getSeuilFormObject({
      store: this.settingsSeuilsRepository,
      typeMarcherOptions: this.typeMarcherOptions,
      isUpdateMode: true, // Disable select fields in update mode
    });
  }

  getSeuils() {
    this.loading = true;
    const params = {
      page: this.serverQuery.page(),
      size: this.serverQuery.pageSize(),
      sort: this.serverQuery.sortBy() ? `${this.serverQuery.sortBy()},${this.serverQuery.sortOrder()}` : undefined,
      search: this.serverQuery.search(),
    };

    this.settingsSeuilsService.getSeuils(params).subscribe({
      next: response => {
        this.data = response.content;
        this.totalRecords = response.totalElements;
        this.loading = false;
        this.dataTableObject = getSeuilDataTableStructure({
          store: this.settingsSeuilsRepository,
          createAction:
            this.isInspectMode || !this.authService.hasRoles([UserRole.ADMIN_CENTRAL, UserRole.SUPER_ADMIN])
              ? undefined
              : () => {
                  this.settingsSeuilsRepository.reset();
                  this.addSeuilComponent.toggleVisible();
                },
          rowActions: this.getRowActions(),
          serverQuery: this.serverQuery,
        });
      },
      error: () => {
        this.loading = false;
        this.totalRecords = 0;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.seuils.message.error.loading'),
        });
      },
    });
  }

  addSeuil() {
    const dto = this.settingsSeuilsRepository.get('dto') as any;

    this.settingsSeuilsService.addSeuil(dto).subscribe({
      next: response => {
        if (response) {
          this.settingsSeuilsRepository.set('response', response);
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.seuils.message.success.added'),
        });
        this.addSeuilComponent.toggleVisible();
        this.getSeuils();
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('settings.seuils.message.error.duplicated'),
        });
      },
    });
  }

  updateSeuil() {
    const seuil = this.settingsSeuilsRepository.get('response') as any;
    const dto = this.settingsSeuilsRepository.get('dto') as any;

    if (!seuil || !seuil.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: this.translate.instant('settings.seuils.message.error.updated'),
      });
      return;
    }

    this.settingsSeuilsService.updateSeuil(seuil.id, dto).subscribe({
      next: response => {
        if (response) {
          this.settingsSeuilsRepository.set('response', response);
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.seuils.message.success.updated'),
        });
        this.getSeuils();
        this.updateSeuilComponent.toggleVisible();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.seuils.message.error.updated'),
        });
      },
    });
  }

  deleteSeuil() {
    const seuil = this.settingsSeuilsRepository.get('response') as any;

    if (!seuil || !seuil.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: this.translate.instant('settings.seuils.message.error.deleted'),
      });
      return;
    }

    this.settingsSeuilsService.deleteSeuil(seuil.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.seuils.message.success.deleted'),
        });
        this.getSeuils();
        this.deleteDialogComponent.toggleVisible();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.seuils.message.error.deleted'),
        });
      },
    });
  }
}
